// frame: GQRct(移动)/ KVHpM(复制)@ ui/dogdisk-user.pen
// spec: docs/ui/user-page-flows/files.md
//
// 移动/复制目标选择(全屏页样式)。多级下钻 crumbs state,面包屑从根到当前。
// 不支持 ESC 关闭(全屏操作页,蒙层点击/返回按钮关闭;有意为之)。
// mode="move": POST /gapi/user/files/move-batch {file_ids, target_parent_id}(单/多文件统一)
// mode="copy": 逐个 POST /gapi/user/files/{id}/copy {target_parent_id}(后端无批量复制端点;
//              文件夹递归/超限/异步阈值由后端处理)
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient, getSupabase, cn } from '@dogdisk/shared'

type FolderRow = {
  id: string
  name: string
  parent_id: string | null
  subtype: string | null
}

export type MoveFileDialogProps = {
  files: { id: string; name: string; parent_id: string | null }[]
  mode: 'move' | 'copy'
  onClose: () => void
  onDone?: () => void
}

type Crumb = { id: string | null; name: string; subtype?: string | null }

function useFolderList(parentId: string | null) {
  return useQuery({
    queryKey: ['move-target', parentId] as const,
    queryFn: async (): Promise<FolderRow[]> => {
      const sb = getSupabase()
      // owner 收口走 v_file_info_mine(内建 user_id=auth.uid() + status='active');目标只能是自己的文件夹。
      let q = sb
        .from('v_file_info_mine')
        .select('id, name, parent_id, subtype')
        .eq('type', 'folder')
        .order('name', { ascending: true })
        .limit(200)
      q = parentId === null ? q.is('parent_id', null) : q.eq('parent_id', parentId)
      const { data, error } = await q
      if (error) return []
      return (data as FolderRow[]) ?? []
    },
  })
}

export function MoveFileDialog({ files, mode, onClose, onDone }: MoveFileDialogProps) {
  const qc = useQueryClient()
  const [crumbs, setCrumbs] = useState<Crumb[]>([{ id: null, name: '我的网盘' }])
  const currentId = crumbs[crumbs.length - 1]?.id ?? null
  const { data: folders = [], isLoading } = useFolderList(currentId)
  const [newFolderInput, setNewFolderInput] = useState(false)
  const [newFolderName, setNewFolderName] = useState('')

  const isCopy = mode === 'copy'
  const frameId = isCopy ? 'KVHpM' : 'GQRct'
  const title = isCopy ? '复制到' : '移动到'
  const selectedIds = new Set(files.map((f) => f.id))
  // 移动:目标=当前 parent 时无意义;复制:同目录允许(后端自动加 "(2)" 后缀)
  const sameParent = !isCopy && files.every((f) => (f.parent_id ?? null) === currentId)
  // 资源包根目录(pack_root)不允许直接落文件:内容须经包管理增删,复制/移动都禁。
  const atPackRoot = crumbs[crumbs.length - 1]?.subtype === 'pack_root'
  const canSubmit =
    files.length > 0 && !selectedIds.has(currentId ?? '') && !sameParent && !atPackRoot

  const submitMut = useMutation({
    mutationFn: async () => {
      if (isCopy) {
        for (const f of files) {
          await apiClient.post(`/gapi/user/files/${f.id}/copy`, { target_parent_id: currentId })
        }
      } else {
        await apiClient.post('/gapi/user/files/move-batch', {
          file_ids: files.map((f) => f.id),
          target_parent_id: currentId,
        })
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['file', 'list'] })
      onDone?.()
      onClose()
    },
    onError: () => {
      // copy 串行循环可能部分成功:失败也刷新列表,让用户看到实际状态
      void qc.invalidateQueries({ queryKey: ['file', 'list'] })
    },
  })

  const createFolderMut = useMutation({
    mutationFn: () =>
      apiClient.post('/gapi/user/folders', {
        parent_id: currentId,
        name: newFolderName.trim(),
      }),
    onSuccess: () => {
      setNewFolderInput(false)
      setNewFolderName('')
      void qc.invalidateQueries({ queryKey: ['move-target', currentId] })
    },
  })

  return (
    <div
      data-frame-id={frameId}
      data-testid={frameId}
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className="fixed inset-0 z-50 flex bg-black/50"
      onClick={onClose}
    >
      <div className="mx-auto flex h-full w-full max-w-[402px] flex-col bg-white" onClick={(e) => e.stopPropagation()}>
        {/* .pen 7sE0P/R1IEug Header — backBtn | 标题 | newFolderBtn,底 1px 描边 */}
        <header className="flex items-center justify-between border-b border-[#F3F4F6] px-6 pb-3 pt-[max(10px,env(safe-area-inset-top))]">
          <button
            type="button"
            data-testid="DVZ3q"
            onClick={onClose}
            aria-label="返回"
            className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#F6F7F8] active:bg-[#EDEEF0]"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1A1A1A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="m12 19-7-7 7-7" />
              <path d="M19 12H5" />
            </svg>
          </button>
          <h2
            className="flex-1 truncate px-3 text-center text-[18px] font-semibold text-[#1A1A1A]"
            style={{ fontFamily: 'Bricolage Grotesque, ui-sans-serif, system-ui' }}
          >
            {title}
          </h2>
          <button
            type="button"
            data-testid="vMr4s"
            onClick={() => setNewFolderInput(true)}
            aria-label="新建文件夹"
            className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#F6F7F8] active:bg-[#EDEEF0]"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1A1A1A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-8.5a2 2 0 0 1-1.7-.9l-.81-1.22A2 2 0 0 0 7.93 3H4Z" />
              <path d="M12 11v6" />
              <path d="M9 14h6" />
            </svg>
          </button>
        </header>

        {/* .pen sdWIe/S027M Breadcrumb */}
        <nav className="flex flex-wrap items-center gap-2 px-6 py-3 text-[14px] font-medium">
          {crumbs.map((c, i) => {
            const last = i === crumbs.length - 1
            return (
              <span key={`${c.id ?? 'root'}-${i}`} className="flex items-center gap-2">
                {i > 0 && (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 18l6-6-6-6" />
                  </svg>
                )}
                <button
                  type="button"
                  className={cn('max-w-[100px] truncate', last ? 'text-[#6B7280]' : 'text-[#19caad]')}
                  onClick={() => setCrumbs((cs) => cs.slice(0, i + 1))}
                >
                  {c.name}
                </button>
              </span>
            )
          })}
        </nav>

        {newFolderInput && (
          <div className="flex gap-2 px-6 pb-3">
            <input
              autoFocus
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && newFolderName.trim() && !createFolderMut.isPending) createFolderMut.mutate()
              }}
              placeholder="新文件夹名称"
              maxLength={64}
              className="h-10 flex-1 rounded-lg bg-[#F1F2F4] px-3 text-[13px] outline-none placeholder:text-[#B4B7BC]"
            />
            <button
              type="button"
              disabled={createFolderMut.isPending || !newFolderName.trim()}
              onClick={() => createFolderMut.mutate()}
              className="rounded-lg bg-[#19caad] px-4 text-[13px] font-medium text-white disabled:opacity-50"
            >
              创建
            </button>
            <button
              type="button"
              onClick={() => setNewFolderInput(false)}
              className="rounded-lg bg-[#F1F2F4] px-3 text-[13px] text-[#1A1A1A]"
            >
              取消
            </button>
          </div>
        )}

        {/* .pen m4O1d/o9YmEb FolderList — h72 rounded-16 灰底行 */}
        <ul className="flex flex-1 flex-col gap-3 overflow-y-auto px-6 pb-3">
          {isLoading ? (
            <li className="py-12 text-center text-[13px] text-[#9CA3AF]">加载中…</li>
          ) : folders.length === 0 ? (
            <li className="py-12 text-center text-[13px] text-[#9CA3AF]">此目录下没有文件夹</li>
          ) : (
            folders.map((f, i) => {
              // folderItem1~4 对应 .pen k4nfj/mI8WL/pzQle/mgMa5(KVHpM 同构,共用 testid)
              const ids = ['k4nfj', 'mI8WL', 'pzQle', 'mgMa5']
              const testid = ids[i] ?? `folder-${i}`
              const disabled = selectedIds.has(f.id)
              return (
                <li key={f.id}>
                  <button
                    type="button"
                    data-testid={testid}
                    disabled={disabled}
                    onClick={() => setCrumbs((cs) => [...cs, { id: f.id, name: f.name, subtype: f.subtype }])}
                    className="flex h-[72px] w-full items-center justify-between rounded-2xl bg-[#F6F7F8] p-4 text-left active:bg-[#EDEEF0] disabled:opacity-40"
                  >
                    <span className="flex min-w-0 items-center gap-[14px]">
                      <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#60A5FA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M4 4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-8.5a2 2 0 0 1-1.7-.9l-.81-1.22A2 2 0 0 0 7.93 3H4Z" />
                      </svg>
                      <span className="flex min-w-0 flex-col gap-1">
                        <span className="truncate text-[15px] text-[#1A1A1A]">{f.name}</span>
                        <span className="text-[12px] text-[#9CA3AF]">文件夹</span>
                      </span>
                    </span>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2">
                      <path d="M9 18l6-6-6-6" />
                    </svg>
                  </button>
                </li>
              )
            })
          )}
        </ul>

        {/* .pen rXkzb/HXESi Footer — 胶囊确认按钮 */}
        <div className="px-6 pt-4" style={{ paddingBottom: 'max(34px, env(safe-area-inset-bottom))' }}>
          {submitMut.error && (
            <p className="pb-2 text-[12px] text-[#FF6B6B]">
              {submitMut.error instanceof Error ? submitMut.error.message : `${title}失败`}
            </p>
          )}
          <button
            type="button"
            data-testid="dn91l"
            disabled={!canSubmit || submitMut.isPending}
            onClick={() => submitMut.mutate()}
            className="flex h-[52px] w-full items-center justify-center gap-2 rounded-full bg-[#19CAAD] text-[16px] font-semibold text-white disabled:opacity-50"
          >
            {isCopy ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <rect x="8" y="8" width="14" height="14" rx="2" ry="2" />
                <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M2 9V5a2 2 0 0 1 2-2h3.9a2 2 0 0 1 1.69.9l.81 1.2a2 2 0 0 0 1.67.9H20a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2" />
                <path d="M2 13h10" />
                <path d="m9 16 3-3-3-3" />
              </svg>
            )}
            {submitMut.isPending
              ? `${isCopy ? '复制' : '移动'}中…`
              : canSubmit
                ? `${isCopy ? '复制' : '移动'}到此处`
                : atPackRoot
                  ? '不能直接放到资源包'
                  : sameParent
                    ? '已在此目录'
                    : `${title}此处`}
          </button>
        </div>
      </div>
    </div>
  )
}
